/* ==========================================================================
   Card Press Interaction (premium-safe)
   Lightweight micro-press for product / collection cards.
   - No mutation observers
   - No hold-state animation
   - Cancels on drag/scroll-like movement
   - Ignores swatches, quick-add, form controls, and slider arrows
   ========================================================================== */

(() => {
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (REDUCED_MOTION_QUERY.matches) return;

  const CARD_SELECTOR = '.card.card--product, .card-collection';
  const IGNORE_SELECTOR = [
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'label',
    '[role="button"]',
    '[data-card-swatch]',
    '.card__swatch',
    '.card__swatches',
    '.card__quick-add',
    '[data-quick-add-trigger]',
    '[data-quick-add-close]',
    '.variant-picker__swatches',
    '.grid-slider__btn',
    '.swiper-button-prev',
    '.swiper-button-next',
  ].join(', ');

  const CLASS_PRESSED = 'is-card-pressed';
  const CLASS_RELEASING = 'is-card-press-releasing';
  const DRAG_CANCEL_THRESHOLD = 10;
  const RELEASE_DURATION_MS = 260;

  /** @type {{ card: HTMLElement; pointerId: number; startX: number; startY: number; releaseTimer: number | null } | null} */
  let activePress = null;

  const clearReleaseTimer = (card) => {
    const timerId = Number(card.dataset.cardPressReleaseTimer || '0');
    if (timerId > 0) {
      window.clearTimeout(timerId);
      delete card.dataset.cardPressReleaseTimer;
    }
  };

  const removePressClasses = (card) => {
    card.classList.remove(CLASS_PRESSED, CLASS_RELEASING);
  };

  const getPressCard = (target) => {
    if (!(target instanceof Element)) return null;
    if (target.closest(IGNORE_SELECTOR)) return null;
    const card = target.closest(CARD_SELECTOR);
    return card instanceof HTMLElement ? card : null;
  };

  const cancelActivePress = ({ withRelease } = { withRelease: false }) => {
    if (!activePress) return;

    const { card } = activePress;
    clearReleaseTimer(card);
    card.classList.remove(CLASS_PRESSED);

    if (withRelease) {
      card.classList.add(CLASS_RELEASING);
      const timer = window.setTimeout(() => {
        card.classList.remove(CLASS_RELEASING);
        delete card.dataset.cardPressReleaseTimer;
      }, RELEASE_DURATION_MS);
      card.dataset.cardPressReleaseTimer = String(timer);
    } else {
      card.classList.remove(CLASS_RELEASING);
    }

    activePress = null;
  };

  const onPointerDown = (event) => {
    if (!event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const card = getPressCard(event.target);
    if (!card) return;

    if (activePress && activePress.card !== card) {
      cancelActivePress();
    }

    clearReleaseTimer(card);
    removePressClasses(card);
    card.classList.add(CLASS_PRESSED);

    activePress = {
      card,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      releaseTimer: null,
    };
  };

  const onPointerMove = (event) => {
    if (!activePress || event.pointerId !== activePress.pointerId) return;

    const dx = Math.abs(event.clientX - activePress.startX);
    const dy = Math.abs(event.clientY - activePress.startY);

    if (dx > DRAG_CANCEL_THRESHOLD || dy > DRAG_CANCEL_THRESHOLD) {
      cancelActivePress({ withRelease: false });
    }
  };

  const onPointerUp = (event) => {
    if (!activePress || event.pointerId !== activePress.pointerId) return;
    cancelActivePress({ withRelease: true });
  };

  const onPointerCancel = (event) => {
    if (!activePress || event.pointerId !== activePress.pointerId) return;
    cancelActivePress({ withRelease: false });
  };

  const onWindowBlur = () => {
    cancelActivePress({ withRelease: false });
  };

  document.addEventListener('pointerdown', onPointerDown, { passive: true });
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerup', onPointerUp, { passive: true });
  document.addEventListener('pointercancel', onPointerCancel, { passive: true });
  window.addEventListener('blur', onWindowBlur);
})();
